import { EventInput, ShiftInput } from '@amzn/red-velvet-api';
import { useUpdateEventDraft } from './draftEvent';
import { useCreateEvent } from './createEvent';
import { useRef } from 'react';

interface UseAutoSaveEventDraftProps {
  eventId: React.MutableRefObject<string>;
  setHasUnsavedChanges: (value: boolean) => void;
  onShiftIdsSynced?: (shiftIds: string[]) => void;
}

export function useAutoSaveEventDraft({
  eventId,
  setHasUnsavedChanges,
  onShiftIdsSynced,
}: UseAutoSaveEventDraftProps) {
  const { mutate: updateEventDraft } = useUpdateEventDraft();
  const { mutate: createEvent } = useCreateEvent();
  const isCreating = useRef<boolean>(false);

  const saveDraft = (event?: EventInput, shifts?: ShiftInput[]) => {
    if (eventId.current === '') return;
    setHasUnsavedChanges(true);
    updateEventDraft(
      { eventId: eventId.current, ...(event && { event }), ...(shifts && { shifts }) },
      {
        onSuccess: (response) => {
          setHasUnsavedChanges(false);
          if (response?.shiftIds?.length && onShiftIdsSynced) {
            onShiftIdsSynced(response.shiftIds);
          }
        },
        // TODO: Display a user-facing error notification (e.g. flashbar) when draft save fails
        onError: () => console.log('Failed to update the draft event'),
      }
    );
  };

  const autoSaveHandler = (updatedEventDetails: EventInput) => {
    setHasUnsavedChanges(true);

    // An empty eventId means that this is the first time the event is being saved
    if (updatedEventDetails.title !== '' && eventId.current === '' && !isCreating.current) {
      isCreating.current = true;
      createEvent(
        { title: updatedEventDetails.title as string },
        {
          onSuccess: (response) => {
            eventId.current = response.eventId;
            setHasUnsavedChanges(false);
            isCreating.current = false;
          },
          // TODO: Display a user-facing error notification (e.g. flashbar) when draft creation fails
          onError: () => {
            isCreating.current = false;
            console.log('Failed to create the draft event');
          },
        }
      );
    } else {
      saveDraft(updatedEventDetails);
    }
  };

  const autoSaveShiftsHandler = (shifts: ShiftInput[]) => {
    saveDraft(undefined, shifts);
  };

  return { autoSaveHandler, autoSaveShiftsHandler };
}
